import { Box, Collapse, Stack } from "@mui/material";
import { type ReactNode, useState } from "react";

import { IconButton } from "../../components/IconButton";
import { Pill } from "../../components/Pill";
import { Text } from "../../components/Text";
import { useTu } from "@src/appV2/i18n/useTu";

interface ShiftDetailsInfoRowProps {
  /**
   * Left-side avatar/icon element (e.g., TimeSlotAvatar, dollar icon)
   */
  avatar: ReactNode;
  /**
   * Primary text (e.g., "7:00am - 3:00pm", "$39/hr")
   */
  primaryText: string;
  /**
   * Secondary text (e.g., "Monday, Jan 12, 2025", "Charge Rate")
   */
  secondaryText: string;
  /**
   * Optional action button on the right (e.g., edit button)
   */
  actionButton?: ReactNode;
  /**
   * Optional expandable content (e.g., charge breakdown)
   * When provided, shows a chevron to expand/collapse
   */
  expandableContent?: ReactNode;
  /**
   * When true, displays the primary text in error color
   */
  isError?: boolean;
  /**
   * Optional badge/pill text displayed next to the primary text (e.g., "Smart Pricing")
   */
  badge?: string;
}

/**
 * Generic info row component for shift details.
 * Used for time section, charge rate section, and similar patterns.
 *
 * This is a content-only component - wrap it in ShiftDetailsCard for styling.
 */
export function ShiftDetailsInfoRow(props: ShiftDetailsInfoRowProps) {
  const { tu } = useTu("schedule");
  const { avatar, primaryText, secondaryText, actionButton, expandableContent, isError, badge } =
    props;
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent = Boolean(expandableContent);

  function handleToggleExpand() {
    setIsExpanded(!isExpanded);
  }

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background?.standout,
      })}
    >
      {/* Main row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 4,
          cursor: hasExpandableContent ? "pointer" : "default",
        }}
        onClick={hasExpandableContent ? handleToggleExpand : undefined}
      >
        {/* Left avatar */}
        {avatar}

        {/* Text content */}
        <Stack sx={{ gap: 0, flex: 1 }}>
          {badge && (
            <Pill
              size="small"
              sx={(theme) => ({
                alignSelf: "flex-start",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
                fontSize: theme.typography.caption.fontSize,
              })}
            >
              {badge}
            </Pill>
          )}
          <Text
            medium
            variant="body1"
            sx={(theme) => ({
              color: isError ? theme.palette.error?.main : theme.palette.base?.strong,
            })}
          >
            {primaryText}
          </Text>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.base?.muted,
            })}
          >
            {secondaryText}
          </Text>
        </Stack>

        {/* Right-side action buttons */}
        <Stack direction="row" spacing={2} alignItems="center">
          {actionButton}
          {hasExpandableContent && (
            <IconButton
              aria-label={isExpanded ? tu("Collapse") : tu("Expand")}
              iconType={isExpanded ? "chevron-up" : "chevron-down"}
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                handleToggleExpand();
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Expandable content */}
      {hasExpandableContent && (
        <Collapse in={isExpanded}>
          <Box
            sx={(theme) => ({
              paddingBottom: 2,
              backgroundColor: theme.palette.background?.standout,
            })}
          >
            {expandableContent}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
